import {
  getAiChatCitationItem,
  getAiChatMessageItem,
  getAiChatSuggestionItem,
  getAiChatThreadItem,
} from '@aline/routes';
import { z } from 'zod';

interface ChatCitationItem {
  // Must use index vs. locationId b/c locationId might be cited multiple times in the same message.
  index: number;
  messageId: string;
  threadId: string;
  type: 'citation';
}
interface ChatSuggestionItem {
  groupId: string;
  messageId: string;
  threadId: string;
  type: 'suggestion';
}
interface ChatMessageItem {
  messageId: string;
  threadId: string;
  type: 'message';
}
interface ChatThreadItem {
  threadId: string;
  type: 'thread';
}
export type AIChatItem = ChatCitationItem | ChatSuggestionItem | ChatMessageItem | ChatThreadItem;

/**
 * Creates an encoded string representation of an AI chat item for use in URLs.
 *
 * The `locationId` is optional and will create an alternate encoding without
 * the `loc` part of the string
 */
export function createAIChatItem(item: AIChatItem): string {
  switch (item.type) {
    case 'thread': {
      return getAiChatThreadItem(item);
    }
    case 'citation': {
      return getAiChatCitationItem(item);
    }
    case 'suggestion': {
      return getAiChatSuggestionItem(item);
    }
    case 'message': {
      return getAiChatMessageItem(item);
    }
  }
}

const citationItemSchema = z.tuple([
  z.literal('thread'),
  z.string(),
  z.literal('message'),
  z.string(),
  z.literal('index'),
  z.coerce.number(),
]);
const suggestionItemSchema = z.tuple([
  z.literal('thread'),
  z.string(),
  z.literal('message'),
  z.string(),
  z.literal('group'),
  z.string(),
]);
const messageItemSchema = z.tuple([
  z.literal('thread'),
  z.string(),
  z.literal('message'),
  z.string(),
]);
const threadItemSchema = z.tuple([z.literal('thread'), z.string()]);
// threadItemSchema must be LAST in the union to not trip-up Zod length checks on the tuple schemas.
const aiChatItemSchema = z.union([
  citationItemSchema,
  suggestionItemSchema,
  messageItemSchema,
  threadItemSchema,
]);

/**
 * Parses an encoded AI chat item string back into its component parts.
 */
export function parseAIChatItem(encodedString: string): AIChatItem | null {
  const parts = encodedString.split('/');
  const result = aiChatItemSchema.safeParse(parts);
  if (!result.success) {
    console.error(result.error);
    return null;
  }

  // Handle thread-only case (2 parts: ['thread', 'threadId'])
  if (parts.length === 2) {
    const [, threadId] = result.data;
    return {
      threadId,
      type: 'thread',
    };
  }

  const [, threadId, , messageId, , groupIdOrIndex] = result.data;

  switch (typeof groupIdOrIndex) {
    case 'string': {
      if (!messageId) return null;
      return {
        groupId: groupIdOrIndex,
        messageId,
        threadId,
        type: 'suggestion',
      };
    }
    case 'number': {
      if (!messageId) return null;
      return {
        index: groupIdOrIndex,
        messageId,
        threadId,
        type: 'citation',
      };
    }
    case 'undefined': {
      if (!messageId) return null;
      return {
        messageId,
        threadId,
        type: 'message',
      };
    }
  }
}
